import { useState, useEffect, useCallback } from "react";
import { G, ADMIN_API } from "../../design/tokens";

const MODULE_COLORS = {
  chatbot: { bg: "rgba(6,182,212,0.13)",   text: "#67E8F9", border: "rgba(6,182,212,0.3)",   label: "ChatBot"        },
  kb:      { bg: "rgba(37,99,235,0.13)",   text: "#93C5FD", border: "rgba(37,99,235,0.3)",   label: "Knowledge Base" },
  leads:   { bg: "rgba(124,58,237,0.13)",  text: "#C4B5FD", border: "rgba(124,58,237,0.3)",  label: "Leads"          },
  users:   { bg: "rgba(5,150,105,0.13)",   text: "#34D399", border: "rgba(5,150,105,0.3)",   label: "Users"          },
  system:  { bg: "rgba(100,116,139,0.13)", text: "#94A3B8", border: "rgba(100,116,139,0.3)", label: "System"         },
};

const ACTION_LABELS = {
  "chatbot.toggle": "Toggled availability",
  "chatbot.rename": "Renamed bot",
  "kb.create":      "Created file",
  "kb.update":      "Updated file",
  "kb.delete":      "Deleted file",
  "kb.upload":      "Uploaded file",
  "lead.status":    "Changed lead status",
  "lead.comment":   "Added comment",
  "user.create":    "Created user",
  "user.update":    "Updated user",
  "user.delete":    "Deleted user",
};

function formatDetail(action, details = {}) {
  switch (action) {
    case "chatbot.toggle": return details.to ? "→ Online" : "→ Offline";
    case "chatbot.rename": return `"${details.from}" → "${details.to}"`;
    case "kb.create":
    case "kb.update":
    case "kb.delete":
    case "kb.upload":    return details.key || "";
    case "lead.status":  return `${details.from || "?"} → ${details.to || "?"}`;
    case "lead.comment": {
      const c = details.comment || "";
      return c ? `"${c.slice(0, 60)}${c.length > 60 ? "…" : ""}"` : "";
    }
    case "user.create":
    case "user.delete":  return details.username || "";
    case "user.update":  return details.username ? `Updated ${details.username}` : "";
    default:             return JSON.stringify(details);
  }
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-PH", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}

const FILTERS = [
  { id: "all",     label: "All"            },
  { id: "chatbot", label: "ChatBot"        },
  { id: "kb",      label: "Knowledge Base" },
  { id: "leads",   label: "Leads"          },
  { id: "users",   label: "Users"          },
];

const TH = { fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", padding: "12px 16px", textAlign: "left", whiteSpace: "nowrap", background: "rgba(4,10,24,0.6)", borderBottom: "1px solid rgba(255,255,255,0.05)" };
const TD = { padding: "11px 16px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "rgba(255,255,255,0.75)", borderBottom: "1px solid rgba(255,255,255,0.03)", verticalAlign: "middle" };

export default function AuditModule({ authHeader }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("all");
  const [search, setSearch]   = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${ADMIN_API}/audit`, { headers: authHeader });
      const data = await res.json();
      setLogs(data.logs || []);
    } catch {}
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const today      = new Date().toISOString().slice(0, 10);
  const todayCount = logs.filter(l => (l.createdAt || "").startsWith(today)).length;

  const visible = logs.filter(l => {
    if (filter !== "all" && l.module !== filter) return false;
    if (search && !l.actor?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ padding: "32px 32px 48px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1 }}>Audit Trail</div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
            {logs.length} total events · {todayCount} today
          </div>
        </div>
        <button type="button" onClick={load} style={{ padding: "8px 18px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(255,255,255,0.6)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, cursor: "pointer" }}>
          ↻ Refresh
        </button>
      </div>

      {/* Filters + Search */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {FILTERS.map(f => (
            <button key={f.id} type="button" onClick={() => setFilter(f.id)}
              style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${filter === f.id ? "#2563EB" : "rgba(255,255,255,0.08)"}`, background: filter === f.id ? "rgba(37,99,235,0.15)" : "transparent", color: filter === f.id ? "#93C5FD" : "rgba(255,255,255,0.4)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11.5, cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s" }}>
              {f.label}
            </button>
          ))}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter by actor…"
          style={{ padding: "7px 14px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(6,14,30,0.6)", color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, outline: "none", width: 180 }} />
      </div>

      {/* Table */}
      <div style={{ background: G.glass, border: `1px solid ${G.glassBrd}`, borderRadius: 16, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", fontFamily: "'Space Grotesk', sans-serif", color: "rgba(255,255,255,0.25)" }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", fontFamily: "'Space Grotesk', sans-serif", color: "rgba(255,255,255,0.25)" }}>No audit events found.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={TH}>Time</th>
                  <th style={TH}>Module</th>
                  <th style={TH}>Action</th>
                  <th style={TH}>Actor</th>
                  <th style={TH}>Details</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((log, i) => {
                  const mc = MODULE_COLORS[log.module] || MODULE_COLORS.system;
                  return (
                    <tr key={log.auditId || i}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(37,99,235,0.06)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                      <td style={{ ...TD, color: "rgba(255,255,255,0.4)", fontSize: 11.5, whiteSpace: "nowrap" }}>{fmtTime(log.createdAt)}</td>
                      <td style={TD}>
                        <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: mc.bg, color: mc.text, border: `1px solid ${mc.border}`, whiteSpace: "nowrap" }}>{mc.label}</span>
                      </td>
                      <td style={{ ...TD, fontWeight: 600, color: "#fff" }}>{ACTION_LABELS[log.action] || log.action}</td>
                      <td style={{ ...TD, color: "#93C5FD" }}>{log.actor || "—"}</td>
                      <td style={{ ...TD, color: "rgba(255,255,255,0.5)", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {formatDetail(log.action, log.details)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
